<template>
  <div class="ine-card skill-plan-actions">
    <ActionLoader v-if="isLoading" class="skill-plan-actions__loader" />
    <template v-else>
      <div
        v-if="!isPlanInCancelledState"
        id="skill-plan-addons-container"
        class="skill-plan-actions__addons-container"
      >
        <div
          v-for="learningArea in availableLearningAreas"
          :key="learningArea.item_code"
          :class="[
            'skill-plan-actions__addons',
            canAddLearningArea(learningArea.item_code) &&
              'skill-plan-actions__addon_disabled',
          ]"
          @click="
            handleSDLAEllipsisClick(canAddLearningArea(learningArea.item_code))
          "
        >
          <div
            :class="[
              'skill-plan-actions__ellipsis',
              `${learningArea.item_code}`,
            ]"
          />
          <span
            v-if="canAddLearningArea(learningArea.item_code)"
            v-tooltip="{
              content: `${
                contactSupportToUpgrade || isSkillDiveFromSupport
                  ? 'Contact INE Support to add learning area'
                  : 'Add learning area'
              }`,
              placement: 'bottom-center',
            }"
            >{{ learningArea.name }}</span
          >
          <span v-else>{{ learningArea.name }}</span>
        </div>
      </div>
      <div class="skill-plan-actions__actions">
        <div
          v-if="isPlanInCancelledState"
          class="skill-plan-actions__reactivate-wrapper"
        >
          <div class="skill-plan-actions__cancel-info">
            <p class="skill-plan-actions__cancel-info--text">
              <AlertTriangleIcon
                class="skill-plan-actions__cancel-info--icon"
              />
              Your subscription has been cancelled. You will continue to have
              access until {{ planExpiryDate }}
            </p>
          </div>
          <button
            id="reactivate"
            class="btn btn--primary"
            @click="$emit('reactivate-renewal-clicked')"
          >
            Reactivate Subscription
          </button>
        </div>
        <a
          v-if="
            (!isPlanInCancelledState &&
              validAddons.length > 0 &&
              contactSupportToUpgrade) ||
              isSkillDiveFromSupport
          "
          id="contact-support"
          target="_blank"
          href="mailto:support@ine.com"
          rel="noopener"
          class="btn btn--primary"
        >
          {{
            isSkillDiveFromSupport
              ? 'Contact support'
              : 'Contact support to upgrade'
          }}
        </a>
        <button
          v-if="
            !isPlanInCancelledState &&
              !contactSupportToUpgrade &&
              !isSkillDiveFromSupport
          "
          id="manage-skill-dive"
          class="btn btn--primary"
          @click="$emit('manage-skill-dive-clicked')"
        >
          Manage Skill Dive
        </button>
        <button
          v-if="isPlanActive && renewal"
          id="cancel"
          class="btn btn--tertiary"
          @click="$emit('cancel-renewal-clicked')"
        >
          Cancel subscription renewal
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import AlertTriangleIcon from '@/assets/icons/subscriptions/alert-triangle.svg'

export default {
  name: 'SkillDivePlanActions',
  components: {
    ActionLoader,
    AlertTriangleIcon: { ...AlertTriangleIcon, name: 'AlertTriangleIcon' },
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    validAddons: {
      type: Array,
      required: true,
    },
    isPlanActive: {
      type: Boolean,
      required: true,
    },
    isPlanInCancelledState: {
      type: Boolean,
      required: true,
    },
    planExpiryDate: {
      type: String,
      required: true,
    },
    renewal: {
      type: Object,
      required: false,
    },
    isSkillDiveFromSupport: {
      type: Boolean,
      required: true,
    },
    skillDivePasses: {
      type: Array,
      required: true,
    },
  },
  data: () => {
    return {
      availableLearningAreas: [
        { item_code: 'skill_dive_cyber', name: 'Cyber Security' },
        { item_code: 'skill_dive_networking', name: 'Networking' },
        { item_code: 'skill_dive_cloud', name: 'Cloud' },
      ],
    }
  },
  computed: {
    ...mapState({
      validPlans: state => state.subscriptions.validPlans,
      isUpgradePlanAllowed: state => state.subscriptions.isUpgradePlanAllowed,
    }),
    ...mapGetters({
      contactSupportToUpgrade: 'app/contactSupportToUpgrade',
    }),
  },
  methods: {
    canAddLearningArea(item_code) {
      if (this.isSkillDiveFromSupport) {
        return !this.skillDivePasses.some(pass => pass.addon_code === item_code)
      } else {
        return this.validAddons.some(addon => addon.item_code === item_code)
      }
    },
    handleSDLAEllipsisClick(canAddLearningArea) {
      if (canAddLearningArea && !this.isSkillDiveFromSupport) {
        this.$emit('manage-skill-dive-clicked')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.skill-plan-actions {
  display: block;
  padding: 20px;
  justify-content: flex-start;
  align-items: center;
  position: relative;
  min-height: 75px;
  border-radius: 0 0 5px 5px;
  @include mobile {
    display: flex;
    padding: 5px;
    justify-content: flex-end;
    min-width: none;
    min-width: 820px;
  }
  &__loader {
    position: absolute;
    right: 25px;
  }
  &__failed-message {
    color: var(--red);
    font-size: rem(16px);
    font-family: var(--font-bold);
  }
  &__reactivate-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  &__actions {
    display: flex;
    flex-direction: column;
    button {
      width: 100%;
      margin-bottom: 20px;
      &:last-child {
        margin-bottom: 0;
      }
    }
    @include mobile {
      display: flex;
      flex-direction: row-reverse;
      gap: 5px;
      button {
        width: fit-content;
        margin-bottom: 0;
        margin-left: 20px;
        &:last-child {
          margin-bottom: 0;
          margin-left: 0;
        }
      }
    }
  }
  &__cancel-info {
    margin: 0 15px 0 15px;
    &--text {
      display: flex;
      align-items: center;
      color: var(--white);
      margin: 0;
      padding: 0;
      @include desktop {
        white-space: nowrap;
      }
    }
    &--icon {
      fill: var(--orange);
      height: 40px;
      width: 30px;
      margin: 0 5px 0 0;
      display: none;
      @include mobile {
        display: block;
      }
    }
  }
  &__addons-container {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    padding: 15px 0;
    @include mobile {
      width: 100%;
    }
  }
  &__addons {
    display: flex;
    gap: 5px;
    padding: 5px;
    align-items: center;
    &:not(:last-child) {
      border-right: 1px solid var(--tundora-gray);
    }
    span {
      font-size: 12px;
      font-weight: 450;
      line-height: 14px;
      color: var(--white);
    }
  }
  &__addon_disabled {
    opacity: 0.4;
    &:hover {
      cursor: pointer;
      opacity: 1;
    }
  }
  &__ellipsis {
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  .skill_dive_cyber {
    background-color: var(--larea-cyber-security);
  }
  .skill_dive_cloud {
    background-color: var(--larea-cloud);
  }
  .skill_dive_networking {
    background-color: var(--larea-networking);
  }
}
</style>
